import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { lastShippingUpdate, toShippingOptions, type ShippingOption, type ShippingRate, type WeightTier } from '../lib/shipping';

/**
 * Metodi di spedizione con i prezzi correnti (tabella shipping_methods) e le
 * fasce di peso della spedizione con Poste (shipping_weight_tiers).
 * Finché non arrivano `methods` è vuoto: chi mostra un prezzo deve aspettare,
 * non inventarne uno.
 */
export function useShippingMethods() {
  const [methods, setMethods] = useState<ShippingOption[]>([]);
  const [tiers, setTiers] = useState<WeightTier[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [methodsRes, tiersRes] = await Promise.all([
      supabase.from('shipping_methods').select('code, price, free_from, active, sort_order, updated_at'),
      supabase.from('shipping_weight_tiers').select('max_grams, price, updated_at').order('max_grams'),
    ]);
    const error = methodsRes.error ?? tiersRes.error;
    if (error) {
      console.error('Error reading shipping methods:', error);
      setError('Impossibile caricare i costi di spedizione');
    } else {
      setError(null);
      const rates = (methodsRes.data ?? []) as ShippingRate[];
      const weightTiers = ((tiersRes.data ?? []) as WeightTier[]).map((t) => ({ ...t, price: Number(t.price) }));
      setMethods(toShippingOptions(rates));
      setTiers(weightTiers);
      setLastUpdated(lastShippingUpdate([...rates, ...weightTiers]));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /** Solo admin con 2FA: la RLS rifiuta l'update a chiunque altro. */
  const updateMethod = async (
    code: ShippingOption['value'],
    changes: Partial<Pick<ShippingRate, 'price' | 'free_from' | 'active'>>
  ) => {
    const { data, error } = await supabase
      .from('shipping_methods')
      .update({ ...changes, updated_at: new Date().toISOString() })
      .eq('code', code)
      .select('code');
    if (error) throw new Error(error.message);
    // Con RLS un update non autorizzato non dà errore: non tocca nessuna riga.
    if (!data || data.length === 0) throw new Error('Non autorizzato a modificare le spedizioni');
    await refresh();
  };

  /** Solo admin con 2FA, come updateMethod. */
  const updateTier = async (maxGrams: number, price: number) => {
    const { data, error } = await supabase
      .from('shipping_weight_tiers')
      .update({ price, updated_at: new Date().toISOString() })
      .eq('max_grams', maxGrams)
      .select('max_grams');
    if (error) throw new Error(error.message);
    if (!data || data.length === 0) throw new Error('Non autorizzato a modificare le spedizioni');
    await refresh();
  };

  return { methods, tiers, lastUpdated, loading, error, refresh, updateMethod, updateTier };
}
